import { useState, useEffect, useRef } from 'react';

/**
 * Streams a 1-2 sentence AI insight for a single video group.
 *
 * @param {object} params
 * @param {string}   params.videoId        - Video identifier
 * @param {string}   params.queryPhrase    - The search term
 * @param {Array}    params.matchContexts  - [{text, source_type}, ...] (top matches)
 * @param {string}   params.customerKey
 * @param {string}   params.channelUrl
 * @param {string}   params.signedToken    - JWT widget token (alternative to customerKey/channelUrl)
 * @param {string}   params.backendUrl
 * @param {boolean}  params.enabled        - Only stream when true (search complete)
 * @param {number}   params.delayMs        - Stagger delay before starting stream (default 0)
 *
 * @returns {{ insight: string, isStreaming: boolean }}
 */
export function useSearchInsight({
  videoId,
  queryPhrase,
  matchContexts,
  customerKey,
  channelUrl,
  signedToken,
  backendUrl,
  enabled,
  delayMs = 0,
}) {
  const [insight, setInsight] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !videoId || !queryPhrase || !matchContexts || matchContexts.length === 0) {
      return;
    }

    // Cancel any in-flight stream from a previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setInsight('');
    setIsStreaming(true);

    const run = async () => {
      try {
        const body = signedToken
          ? {
              query_phrase: queryPhrase,
              video_id: videoId,
              match_contexts: matchContexts,
              signed_token: signedToken,
            }
          : {
              query_phrase: queryPhrase,
              video_id: videoId,
              match_contexts: matchContexts,
              customer_key: customerKey,
              channel_url: channelUrl,
            };

        const res = await fetch(`${backendUrl}/api/search_insight`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify(body),
          signal: controller.signal,
          credentials: 'include',
        });

        if (!res.ok || !res.body) {
          setIsStreaming(false);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamDone = false;

        // Same SSE parse pattern as LandingPage.js
        const processEvent = (event) => {
          const line = event.split('\n').find((l) => l.startsWith('data: '));
          if (!line) return;

          const payload = line.slice(6).trim();
          if (!payload) return;

          if (payload === '[DONE]') {
            streamDone = true;
            return;
          }

          let data;
          try {
            data = JSON.parse(payload);
          } catch {
            return;
          }

          if (data.error) {
            setInsight('');
            streamDone = true;
            return;
          }

          if (data.token) {
            setInsight((prev) => prev + data.token);
          }
        };

        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.trim()) processEvent(buffer);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const event of events) {
            processEvent(event);
            if (streamDone) break;
          }
        }

        setIsStreaming(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('useSearchInsight stream error:', err);
        }
        setIsStreaming(false);
      }
    };

    const timerId = setTimeout(run, delayMs);

    return () => {
      clearTimeout(timerId);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // matchContexts intentionally excluded: stable after search completes,
    // and array identity changes on every render would cause infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, videoId, queryPhrase, backendUrl, customerKey, channelUrl, signedToken, delayMs]);

  return { insight, isStreaming };
}
